import type { PostDto } from '@_/api-contracts/public-api.ts';
import type { PostFeedProps } from '@_/components';
import type { ApiResponse } from '@_/types';
import type { VanillaLinkProps } from '@_/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PostFeed } from '@_/components';
import { PostType } from '@_/constants';
import { useDialog } from '@_/hooks';
import { Link } from '@_/ui';
import { publicApiClient } from '#src/api.ts';
import { LoginDialog } from '#src/components/dialogs/LoginDialog.tsx';
import { LinkOptionsBuilder } from '#src/lib/LinkOptionsBuilder.ts';
import { useStores } from '#src/state/StoreContext.tsx';

export function EnhancedPostFeed({
  items,
  ...otherProps
}: Pick<PostFeedProps<PostDto>, 'bleed' | 'spacing'> & {
  items: PostDto[];
}) {
  const { useAuthStore } = useStores();
  const isAuthenticated = useAuthStore(state => !!state.session);

  const loginDialog = useDialog({
    component: LoginDialog,
    openFunctionArgsToProps: (data: { onSuccess?: () => void }) => ({
      onSuccess: data.onSuccess
    })
  });

  const [pendingLikes, setPendingLikes] = useState<Map<string, boolean>>(() => new Map());
  const [likedPosts, setLikedPosts] = useState<Map<string, boolean>>(() => new Map());
  const [updatedLikes, setUpdatedLikes] = useState<Map<string, number>>(() => new Map());

  const posts = useMemo(
    () =>
      items.map(post => ({
        ...post,
        hasLiked:
          typeof likedPosts.get(post.id) === 'boolean'
            ? (likedPosts.get(post.id) as boolean)
            : post.hasLiked,
        numLikes:
          typeof updatedLikes.get(post.id) === 'number'
            ? (updatedLikes.get(post.id) as number)
            : post.numLikes
      })),
    [items, updatedLikes, likedPosts]
  );

  // Update voting state and number of likes whenever new items are passed in
  useEffect(() => {
    setLikedPosts(() => new Map(items.map(post => [post.id, post.hasLiked])));
    setUpdatedLikes(() => new Map(items.map(post => [post.id, post.numLikes])));
  }, [items]);

  const toggleLike = useCallback(
    async (post: PostDto) => {
      let result: ApiResponse<{ numLikes: number }>;
      if (!pendingLikes.get(post.id)) {
        setPendingLikes(map => new Map(map.set(post.id, true)));

        if (likedPosts.get(post.id)) {
          result = await publicApiClient.posts.unlike({
            params: { id: post.id }
          });

          setLikedPosts(map => new Map(map.set(post.id, false)));
        } else {
          result = await publicApiClient.posts.like({
            params: { id: post.id }
          });

          setLikedPosts(map => new Map(map.set(post.id, true)));
        }

        setPendingLikes(map => new Map(map.set(post.id, false)));

        setUpdatedLikes(
          map => new Map(map.set(post.id, parseInt(result.data.numLikes as unknown as string, 10)))
        );
      }
    },
    [pendingLikes, likedPosts]
  );

  const loginThenLike = useCallback(
    (post: PostDto) => {
      loginDialog.open({ onSuccess: () => toggleLike(post) });
    },
    [loginDialog, toggleLike]
  );

  return (
    <>
      {loginDialog.element}

      <PostFeed
        {...otherProps}
        getAuthor={getAuthor}
        items={posts}
        onToggleLike={isAuthenticated ? toggleLike : loginThenLike}
        renderCommentLink={renderCommentsLink}
        renderCommentsLink={renderCommentsLink}
        renderTitleLink={renderTitleLink}
      />
    </>
  );
}

function getAuthor(post: PostDto): ReturnType<PostFeedProps<PostDto>['getAuthor']> {
  return {
    avatarUrl: post.author.avatarUrl,
    name: post.author.name,
    renderLink:
      post.author.object === 'organization'
        ? props => (
            <Link
              {...props}
              {...LinkOptionsBuilder.organization({
                organization: post.author
              })}
            />
          )
        : undefined,
    type: post.author.object === 'organization' ? 'organization' : 'person'
  };
}

function renderCommentsLink(post: PostDto, props: VanillaLinkProps) {
  return post.type === PostType.Article ? (
    <Link
      {...props}
      from="/{-$locale}"
      to="/{-$locale}/article/$postId"
      params={{ postId: post.id }}
    />
  ) : (
    <Link
      {...props}
      from="/{-$locale}"
      to="/{-$locale}/post/$postId"
      params={{ postId: post.id }}
    />
  );
}

function renderTitleLink(post: PostDto, props: VanillaLinkProps) {
  return post.type === PostType.Article ? (
    <Link
      {...props}
      from="/{-$locale}"
      to="/{-$locale}/article/$postId"
      params={{ postId: post.id }}
    />
  ) : undefined;
}
